import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTasks, deleteTask, exportTasks, getUsers, getProject } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Search, Plus, Download, MoreVertical, Edit, Trash2, Eye,
  FileSpreadsheet, FileText, FileDown, User, Users, X, ChevronDown, LayoutList, LayoutGrid, CalendarDays, ArrowLeft, FolderKanban
} from 'lucide-react';
import { toast } from 'sonner';
import { KanbanBoard } from '../components/KanbanBoard';

const STATUSES_ALL = ['To Do', 'In Progress', 'In Review', 'Completed', 'Cancelled'];
const STATUSES_HR = ['To Do', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
const STATUSES_NON_DD = ['To Do', 'In Progress', 'Completed', 'Cancelled'];
const CATEGORIES = ['Video/Reel', 'Static', 'Other collateral', 'Website', 'Print Material', 
                   'Events & Exhibition', 'Branding', 'Emailers', 'Digital Ads', 'Presentation'];
const BRANDS = ['United Group', 'Unitile', 'UniVicoustic', 'U-Flex', 'Forza', 'UniPro'];
import { useDepartments } from '../hooks/useDepartments';
const PRIORITIES = [
  { label: 'High (1-3)', values: [1,2,3] },
  { label: 'Medium (4-6)', values: [4,5,6] },
  { label: 'Low (7-10)', values: [7,8,9,10] },
];

const FILTER_STORAGE_KEY = 'tasksFilters';

const saveFilters = (filters) => {
  sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
};

const loadFilters = () => {
  try {
    const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
};

// Multi-select filter component
const MultiFilter = ({ label, options, selected, onChange, testId, renderLabel }) => {
  const count = selected.length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 gap-1.5 text-sm px-3" data-testid={testId}>
          {count === 0 ? label : `${label} (${count})`}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" align="start">
        <div className="space-y-0.5 max-h-60 overflow-y-auto">
          {options.map(opt => {
            const val = typeof opt === 'string' ? opt : opt.value;
            const display = renderLabel ? renderLabel(opt) : (typeof opt === 'string' ? opt : opt.label);
            return (
              <label key={val} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer text-sm">
                <Checkbox
                  checked={selected.includes(val)}
                  onCheckedChange={() => {
                    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
                  }}
                />
                {display}
              </label>
            );
          })}
        </div>
        {count > 0 && (
          <Button variant="ghost" size="sm" className="w-full mt-1 text-xs text-muted-foreground" onClick={() => onChange([])}>
            Clear
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const TasksPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin, isAdmin, isCoordinator } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [tasks, setTasks] = useState([]);
  const [project, setProject] = useState(null);
  const projectFilter = searchParams.get('project');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [scopeToggle, setScopeToggle] = useState(() => {
    return sessionStorage.getItem('tasksScope') || 'my';
  });
  const [viewMode, setViewMode] = useState(() => {
    return sessionStorage.getItem('tasksViewMode') || 'list';
  });

  // Multi-select filter states — restored from sessionStorage
  const stored = loadFilters();
  const [search, setSearch] = useState(stored.search || '');
  const [statusFilter, setStatusFilter] = useState(stored.statusFilter || []);
  const [categoryFilter, setCategoryFilter] = useState(stored.categoryFilter || []);
  const [subCategoryFilter, setSubCategoryFilter] = useState(stored.subCategoryFilter || []);
  const [brandFilter, setBrandFilter] = useState(stored.brandFilter || []);
  const [priorityFilter, setPriorityFilter] = useState(stored.priorityFilter || []);
  const [departmentFilter, setDepartmentFilter] = useState(stored.departmentFilter || []);
  const [assigneeFilter, setAssigneeFilter] = useState(stored.assigneeFilter || []);
  const [workTypeFilter, setWorkTypeFilter] = useState(stored.workTypeFilter || []);
  const [sortBy, setSortBy] = useState(null); // { field: 'work_type', dir: 'asc'|'desc' }
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'custom'
  const [customDate, setCustomDate] = useState('');

  // Persist filters to sessionStorage
  useEffect(() => {
    saveFilters({ search, statusFilter, categoryFilter, subCategoryFilter, brandFilter, priorityFilter, departmentFilter, assigneeFilter, workTypeFilter });
  }, [search, statusFilter, categoryFilter, subCategoryFilter, brandFilter, priorityFilter, departmentFilter, assigneeFilter, workTypeFilter]);

  useEffect(() => {
    fetchTasks();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('tasksScope', scopeToggle);
  }, [scopeToggle]);

  // HR users should never see "department" scope; coerce stale value to "my"
  useEffect(() => {
    if (user?.department === 'Human Resources Management' && scopeToggle === 'department') {
      setScopeToggle('my');
    }
  }, [user?.department, scopeToggle]);

  useEffect(() => {
    sessionStorage.setItem('tasksViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const t = setTimeout(() => fetchTasks(), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (projectFilter) {
      getProject(projectFilter).then(r => setProject(r.data)).catch(() => setProject(null));
    } else {
      setProject(null);
    }
  }, [projectFilter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const pf = searchParams.get('project');
      if (pf) params.project_id = pf;
      const response = await getTasks(params);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await getUsers({ status: 'Active' });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTasks();
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask(taskToDelete.id);
      toast.success('Task deleted successfully');
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await exportTasks(format);
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 
              format === 'csv' ? 'text/csv' : 
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tasks_export.${format === 'excel' ? 'xlsx' : format}`;
      a.click();
      toast.success('Export successful');
    } catch (error) {
      toast.error('Failed to export tasks');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter([]);
    setCategoryFilter([]);
    setSubCategoryFilter([]);
    setBrandFilter([]);
    setPriorityFilter([]);
    setDepartmentFilter([]);
    setAssigneeFilter([]);
    setWorkTypeFilter([]);
    setDateFilter('all');
    setCustomDate('');
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
  };

  const hasActiveFilters = search || statusFilter.length || categoryFilter.length || subCategoryFilter.length || brandFilter.length || priorityFilter.length || departmentFilter.length || assigneeFilter.length || workTypeFilter.length || dateFilter !== 'all';

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-500/15 text-green-600 border-green-500/20';
      case 'In Progress': return 'bg-blue-500/15 text-blue-600 border-blue-500/20';
      case 'In Review': return 'bg-amber-500/15 text-amber-600 border-amber-500/20';
      case 'On Hold': return 'bg-purple-500/15 text-purple-600 border-purple-500/20';
      case 'Cancelled': return 'bg-red-500/15 text-red-600 border-red-500/20';
      default: return 'bg-gray-500/15 text-gray-600 border-gray-500/20';
    }
  };

  const getPriorityBadge = (priority, dept) => {
    const isDD = dept === 'Design & Development';
    let label;
    if (isDD) {
      if (priority === 1) label = 'High';
      else if (priority <= 3) label = 'Medium';
      else label = 'Low';
    } else {
      if (priority <= 3) label = 'High';
      else if (priority <= 6) label = 'Medium';
      else label = 'Low';
    }
    if (label === 'High') return <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/20">High</Badge>;
    if (label === 'Medium') return <Badge className="bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20">Medium</Badge>;
    return <Badge className="bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/20">Low</Badge>;
  };

  // Delete permission: Member can only delete tasks assigned to them
  const canDeleteTask = (task) => {
    if (user?.role === 'Member') return task.assigned_to === user?.id || (task.assigned_to_list || []).includes(user?.id);
    return user?.role !== 'Member';
  };

  const showScopeToggle = !isSuperAdmin && !projectFilter;

  // D&D context: show In Review status / Team UI only when user is in D&D or Super Admin
  const isDDContext = isSuperAdmin || ['Design & Development', 'Marketing', 'Sales Coordinators'].includes(user?.department);
  const isHRContext = isSuperAdmin || user?.department === 'Human Resources Management';
  // Super Admin sees both D&D's In Review AND HR's On Hold; otherwise pick by user dept.
  const STATUSES = isSuperAdmin
    ? ['To Do', 'In Progress', 'In Review', 'On Hold', 'Completed', 'Cancelled']
    : ['Design & Development', 'Marketing', 'Sales Coordinators'].includes(user?.department) ? STATUSES_ALL
    : user?.department === 'Human Resources Management' ? STATUSES_HR
    : STATUSES_NON_DD;

  // Recursive reportee IDs (HR hierarchy): BFS via manager_email (and manager_email_2)
  const recursiveReporteeIds = (() => {
    if (!user?.email) return [];
    const byManager = new Map();
    teamMembers.forEach(m => {
      [m.manager_email, m.manager_email_2].forEach(mgrEmail => {
        const mgr = (mgrEmail || '').toLowerCase();
        if (!mgr) return;
        if (!byManager.has(mgr)) byManager.set(mgr, []);
        byManager.get(mgr).push(m);
      });
    });
    const queue = [user.email.toLowerCase()];
    const collected = new Set();
    while (queue.length) {
      const mgrEmail = queue.shift();
      const directs = byManager.get(mgrEmail) || [];
      for (const m of directs) {
        if (collected.has(m.id)) continue;
        collected.add(m.id);
        if (m.email) queue.push(m.email.toLowerCase());
      }
    }
    return Array.from(collected);
  })();
  const directReporteeIds = recursiveReporteeIds;
  const isHRUser = user?.department === 'Human Resources Management';
  const hasReportees = directReporteeIds.length > 0;

  // For HR, replace "Department Tasks" with "My Team's Tasks". For HR users with no
  // reportees, only "My Tasks" is meaningful — hide the toggle entirely.
  const useTeamMode = isHRUser; // HR -> team mode
  const hideToggleForHRNoReportees = isHRUser && !hasReportees;
  const effectiveScope = (() => {
    // In a project view: Members see only their own; everyone else sees ALL project tasks
    if (projectFilter) return user?.role === 'Member' ? 'my' : 'all';
    // Super Admin always sees ALL tasks regardless of any stale sessionStorage scope
    if (isSuperAdmin) return 'all';
    if (hideToggleForHRNoReportees) return 'my';
    if (useTeamMode) return scopeToggle === 'my' ? 'my' : 'team';
    return scopeToggle; // 'my' | 'department'
  })();

  // Apply scope filter
  let scopedTasks;
  if (effectiveScope === 'my') {
    // My Tasks includes team tasks where the user is any assignee (primary OR team member)
    scopedTasks = tasks.filter(t => t.assigned_to === user?.id || (t.assigned_to_list || []).includes(user?.id));
  } else if (effectiveScope === 'team') {
    scopedTasks = tasks.filter(t =>
      directReporteeIds.includes(t.assigned_to) ||
      (t.assigned_to_list || []).some(id => directReporteeIds.includes(id))
    );
  } else {
    // 'department' for non-HR, or 'all' for Super Admin — no client-side scope filter
    scopedTasks = tasks;
  }

  // In a project view, Members only see their own tasks within the project
  if (projectFilter && user?.role === 'Member') {
    scopedTasks = scopedTasks.filter(t => t.assigned_to === user?.id || (t.assigned_to_list || []).includes(user?.id));
  }

  // Apply multi-select client-side filters
  const filteredTasks = scopedTasks.filter(t => {
    if (dateFilter === 'today') {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
      const createdStr = t.created_at ? t.created_at.slice(0, 10) : '';
      if (createdStr !== todayStr) return false;
    } else if (dateFilter === 'custom' && customDate) {
      const createdStr = t.created_at ? t.created_at.slice(0, 10) : '';
      if (createdStr !== customDate) return false;
    }
    if (statusFilter.length && !statusFilter.includes(t.status)) return false;
    if (categoryFilter.length && !categoryFilter.includes(t.category)) return false;
    if (subCategoryFilter.length && !subCategoryFilter.includes(t.sub_category)) return false;
    if (brandFilter.length && !brandFilter.includes(t.brand)) return false;
    if (departmentFilter.length && !departmentFilter.includes(t.department)) return false;
    if (assigneeFilter.length && !assigneeFilter.includes(t.assigned_to)) return false;
    if (workTypeFilter.length && !workTypeFilter.includes(t.work_type || 'Individual')) return false;
    if (priorityFilter.length) {
      const allVals = priorityFilter.flatMap(p => {
        const found = PRIORITIES.find(pr => pr.label === p);
        return found ? found.values : [];
      });
      if (!allVals.includes(t.priority)) return false;
    }
    return true;
  });

  // Sort: supports work_type (string) and created_at / due_date (dates)
  if (sortBy?.field) {
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    filteredTasks.sort((a, b) => {
      if (sortBy.field === 'work_type') {
        return dir * (a.work_type || 'Individual').localeCompare(b.work_type || 'Individual');
      }
      const av = a[sortBy.field] ? new Date(a[sortBy.field]).getTime() : NaN;
      const bv = b[sortBy.field] ? new Date(b[sortBy.field]).getTime() : NaN;
      const aNan = isNaN(av), bNan = isNaN(bv);
      if (aNan && bNan) return 0;
      if (aNan) return 1;   // empty dates always sort last
      if (bNan) return -1;
      return dir * (av - bv);
    });
  }

  const toggleSort = (field) => {
    setSortBy(prev => prev?.field === field ? (prev.dir === 'asc' ? { field, dir: 'desc' } : null) : { field, dir: 'asc' });
  };

  const statusCounts = {
    all: filteredTasks.length,
    'To Do': filteredTasks.filter(t => t.status === 'To Do').length,
    'In Progress': filteredTasks.filter(t => t.status === 'In Progress').length,
    'Completed': filteredTasks.filter(t => t.status === 'Completed').length,
    'Cancelled': filteredTasks.filter(t => t.status === 'Cancelled').length,
  };

  // Dept-scoped filter options: compute from current dept context
  // Super Admin: filter by selected departmentFilter or user's effective dept.
  // Others: their own department.
  const filterDepts = (() => {
    if (departmentFilter.length > 0) return departmentFilter;
    if (!isSuperAdmin && user?.department) {
      return (user?.departments && user.departments.length) ? user.departments : [user.department];
    }
    return []; // means all (Super Admin no filter)
  })();
  
  const matchesDept = (t) => filterDepts.length === 0 || filterDepts.includes(t.department);
  
  const availableCategories = Array.from(new Set(
    scopedTasks.filter(matchesDept).map(t => t.category).filter(Boolean)
  )).sort();
  const availableSubCategories = Array.from(new Set(
    scopedTasks.filter(matchesDept).map(t => t.sub_category).filter(Boolean)
  )).sort();
  const availableBrands = Array.from(new Set(
    scopedTasks.filter(matchesDept).map(t => t.brand).filter(Boolean)
  )).sort();
  
  // Show Brand filter only when Marketing dept is selected/in-scope
  const showBrandFilter = filterDepts.includes('Marketing') ||
    (filterDepts.length === 0 && availableBrands.length > 0);
  // Show Sub-Category filter only when there are sub_categories in scope (HR, D&D)
  const showSubCategoryFilter = availableSubCategories.length > 0;

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Project context banner */}
      {projectFilter && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/10 px-4 py-3" data-testid="project-context-banner">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#F5A623]/20 flex items-center justify-center shrink-0">
              <FolderKanban className="h-5 w-5 text-[#F5A623]" />
            </div>
            <div>
              <p className="font-heading font-semibold leading-tight" data-testid="project-context-name">{project?.name || 'Project'}</p>
              {project?.description && <p className="text-xs text-muted-foreground line-clamp-1 max-w-xl">{project.description}</p>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/projects')} data-testid="back-to-projects-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> All Projects
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold" data-testid="tasks-page-title">
            {projectFilter ? 'Project Tasks' : isSuperAdmin ? 'All Tasks' : effectiveScope === 'my' ? 'My Tasks' : effectiveScope === 'team' ? "My Team's Tasks" : 'Department Tasks'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {projectFilter ? (user?.role === 'Member' ? 'Your tasks in this project' : 'All tasks in this project') : isSuperAdmin ? 'Manage tasks across all departments' : effectiveScope === 'my' ? 'Tasks assigned to you' : effectiveScope === 'team' ? "Tasks assigned to your team (all reportees)" : `All tasks in ${user?.department || 'your department'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-muted/50 rounded-lg p-0.5" data-testid="view-toggle">
            <button onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="view-list-btn">
              <LayoutList className="h-4 w-4" />List
            </button>
            <button onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'kanban' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="view-kanban-btn">
              <LayoutGrid className="h-4 w-4" />Board
            </button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="h-4 w-4 mr-2" />Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('excel')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="h-4 w-4 mr-2" />CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileDown className="h-4 w-4 mr-2" />PDF Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold">
            <Link to={searchParams.get('project') ? `/tasks/new?project=${searchParams.get('project')}` : '/tasks/new'} data-testid="new-task-btn">
              <Plus className="h-4 w-4 mr-2" />New Task
            </Link>
          </Button>
        </div>
      </div>

      {/* Scope Toggle */}
      {showScopeToggle && !hideToggleForHRNoReportees && (
        <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1 w-fit" data-testid="scope-toggle">
          <button onClick={() => setScopeToggle('my')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${scopeToggle === 'my' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="scope-my">
            <User className="h-4 w-4" />My Tasks
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${scopeToggle === 'my' ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-muted text-muted-foreground'}`}>
              {tasks.filter(t => t.assigned_to === user?.id || (t.assigned_to_list || []).includes(user?.id)).length}
            </span>
          </button>
          {useTeamMode ? (
            <button onClick={() => setScopeToggle('team')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${scopeToggle === 'team' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="scope-team">
              <Users className="h-4 w-4" />My Team's Tasks
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${scopeToggle === 'team' ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-muted text-muted-foreground'}`}>
                {tasks.filter(t => directReporteeIds.includes(t.assigned_to) || (t.assigned_to_list || []).some(id => directReporteeIds.includes(id))).length}
              </span>
            </button>
          ) : (
            <button onClick={() => setScopeToggle('department')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${scopeToggle === 'department' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="scope-department">
              <Users className="h-4 w-4" />Department Tasks
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${scopeToggle === 'department' ? 'bg-[#F5A623]/15 text-[#F5A623]' : 'bg-muted text-muted-foreground'}`}>
                {tasks.length}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search tasks..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-tasks-input" />
            </form>
            <div className="flex flex-wrap items-center gap-2">
              {/* Date Filter Group */}
              <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-0.5" data-testid="date-filter-group">
                <button
                  onClick={() => { setDateFilter(dateFilter === 'today' ? 'all' : 'today'); setCustomDate(''); }}
                  className={`flex items-center gap-1.5 h-8 px-3 rounded-md text-sm font-medium transition-all ${dateFilter === 'today' ? 'bg-[#F5A623] text-black shadow-sm' : 'hover:bg-accent'}`}
                  data-testid="today-tasks-filter-btn"
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  Today
                </button>
                <div className="relative">
                  <input
                    type="date"
                    value={customDate}
                    onChange={(e) => { setCustomDate(e.target.value); setDateFilter(e.target.value ? 'custom' : 'all'); }}
                    className={`h-8 px-2 rounded-md text-sm border-0 bg-transparent cursor-pointer transition-all ${dateFilter === 'custom' ? 'bg-[#F5A623] text-black shadow-sm font-medium' : 'hover:bg-accent text-muted-foreground'}`}
                    data-testid="custom-date-filter"
                  />
                </div>
              </div>
              <MultiFilter label="Status" options={STATUSES} selected={statusFilter} onChange={setStatusFilter} testId="status-filter" />
              <MultiFilter label="Priority" options={PRIORITIES.map(p => p.label)} selected={priorityFilter} onChange={setPriorityFilter} testId="priority-filter" />
              <MultiFilter label="Category" options={availableCategories} selected={categoryFilter} onChange={setCategoryFilter} testId="category-filter" />
              {showSubCategoryFilter && (
                <MultiFilter label="Sub-Category" options={availableSubCategories} selected={subCategoryFilter} onChange={setSubCategoryFilter} testId="subcategory-filter" />
              )}
              {showBrandFilter && (
                <MultiFilter label="Brand" options={availableBrands.length ? availableBrands : BRANDS} selected={brandFilter} onChange={setBrandFilter} testId="brand-filter" />
              )}
              <MultiFilter
                label="Assignee"
                options={teamMembers.map(m => ({ value: m.id, label: m.full_name }))}
                selected={assigneeFilter}
                onChange={setAssigneeFilter}
                testId="assignee-filter"
                renderLabel={(opt) => opt.label}
              />
              {isSuperAdmin && (
                <MultiFilter label="Department" options={DEPARTMENTS} selected={departmentFilter} onChange={setDepartmentFilter} testId="department-filter" />
              )}
              <MultiFilter label="Work Type" options={['Individual', 'Team']} selected={workTypeFilter} onChange={setWorkTypeFilter} testId="work-type-filter" />
              {hasActiveFilters ? (
                <Button variant="ghost" size="sm" className="h-9 text-xs text-muted-foreground hover:text-destructive gap-1" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-3 w-3" />Clear Filters
                </Button>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks View */}
      <Card className="border-border/50">
        <CardContent className={viewMode === 'kanban' ? 'p-4' : 'p-0'}>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="text-center py-12" data-testid="no-tasks-found">
              <p className="text-muted-foreground">No tasks found</p>
              {hasActiveFilters ? (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>Clear Filters</Button>
              ) : (
                <Button asChild className="mt-4" variant="outline">
                  <Link to="/tasks/new">Create your first task</Link>
                </Button>
              )}
            </div>
          ) : viewMode === 'kanban' ? (
            <KanbanBoard tasks={filteredTasks} showInReview={isDDContext} showOnHold={isHRContext} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Title</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => toggleSort('work_type')}
                        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                        data-testid="sort-work-type-btn"
                      >
                        Work Type
                        {sortBy?.field === 'work_type' && (
                          <span className="text-[10px]">{sortBy.dir === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </button>
                    </th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Priority</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Assignee</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => toggleSort('created_at')}
                        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                        data-testid="sort-created-date-btn"
                      >
                        Created Date
                        {sortBy?.field === 'created_at' && (
                          <span className="text-[10px]">{sortBy.dir === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </button>
                    </th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <button
                        type="button"
                        onClick={() => toggleSort('due_date')}
                        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                        data-testid="sort-due-date-btn"
                      >
                        Due Date
                        {sortBy?.field === 'due_date' && (
                          <span className="text-[10px]">{sortBy.dir === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </button>
                    </th>
                    <th className="text-right p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className="border-b border-border hover:bg-accent/50 transition-colors" data-testid={`task-row-${task.id}`}>
                      <td className="p-4">
                        <Link to={`/tasks/${task.id}`} className="font-medium hover:text-[#F5A623] transition-colors" data-testid={`task-link-${task.id}`}>
                          {task.task_name}
                        </Link>
                        {task.project_name && (
                          <span className="flex items-center gap-1 mt-1 w-fit text-[10px] px-1.5 py-0.5 rounded bg-[#F5A623]/15 text-[#F5A623] font-medium" data-testid={`task-project-tag-${task.id}`}>
                            <FolderKanban className="h-2.5 w-2.5" />{task.project_name}
                          </span>
                        )}
                        {task.brand && <p className="text-xs text-muted-foreground mt-1">{task.brand}</p>}
                        {task.entity && <p className="text-[10px] text-muted-foreground">{task.entity}</p>}
                      </td>
                      <td className="p-4"><Badge variant="outline" className="text-xs">{task.category || '-'}</Badge></td>
                      <td className="p-4">
                        {task.work_type === 'Team' ? (
                          <Badge className="bg-blue-500/15 text-blue-600 border border-blue-500/20 text-xs gap-1"><Users className="h-3 w-3 inline" />Team</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs gap-1"><User className="h-3 w-3 inline" />Individual</Badge>
                        )}
                      </td>
                      <td className="p-4">{getPriorityBadge(task.priority, task.department)}</td>
                      <td className="p-4"><Badge className={getStatusColor(task.status)}>{task.status}</Badge></td>
                      <td className="p-4"><span className="text-sm">{task.assigned_to_name || '-'}</span></td>
                      <td className="p-4"><span className="text-sm">{task.created_at ? new Date(task.created_at).toLocaleDateString() : '-'}</span></td>
                      <td className="p-4"><span className="text-sm">{new Date(task.due_date).toLocaleDateString()}</span></td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`task-actions-${task.id}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/tasks/${task.id}`}><Eye className="h-4 w-4 mr-2" />View</Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/tasks/${task.id}/edit`}><Edit className="h-4 w-4 mr-2" />Edit</Link>
                            </DropdownMenuItem>
                            {canDeleteTask(task) && (
                              <DropdownMenuItem onClick={() => { setTaskToDelete(task); setDeleteDialogOpen(true); }} className="text-destructive focus:text-destructive">
                                <Trash2 className="h-4 w-4 mr-2" />Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{taskToDelete?.task_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
